//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

export enum EngagementMetric {
  Impressions = 'IMPRESSION',
  PinClicks = 'PIN_CLICK',
  PinClickRate = 'PIN_CLICK_RATE',
  Saves = 'SAVE',
  SaveRate = 'SAVE_RATE',
  OutboundClicks = 'OUTBOUND_CLICK',
  OutboundClickRate = 'OUTBOUND_CLICK_RATE',
  Engagement = 'ENGAGEMENT',
  EngagementRate = 'ENGAGEMENT_RATE',
}

export const EngagementMetricSortLabels = {
  [EngagementMetric.Impressions]: 'Most Impressions',
  [EngagementMetric.PinClicks]: 'Most Pin Clicks',
  [EngagementMetric.PinClickRate]: 'Highest Pin Click Rate',
  [EngagementMetric.Saves]: 'Most Saves',
  [EngagementMetric.SaveRate]: 'Highest Save Rate',
  [EngagementMetric.OutboundClicks]: 'Most Outbound Clicks',
  [EngagementMetric.OutboundClickRate]: 'Highest Outbound Click Rate',
  [EngagementMetric.Engagement]: 'Most Engagement',
  [EngagementMetric.EngagementRate]: 'Highest Engagement Rate',
};

export const EngagementMetricLabels = {
  [EngagementMetric.Impressions]: 'Impressions',
  [EngagementMetric.PinClicks]: 'Pin Clicks',
  [EngagementMetric.PinClickRate]: 'Pin Click Rate',
  [EngagementMetric.Saves]: 'Saves',
  [EngagementMetric.SaveRate]: 'Save Rate',
  [EngagementMetric.OutboundClicks]: 'Outbound Clicks',
  [EngagementMetric.OutboundClickRate]: 'Outbound Click Rate',
  [EngagementMetric.Engagement]: 'Engagements',
  [EngagementMetric.EngagementRate]: 'Engagement Rate',
};

export enum TopPinsTableView {
  TABLE = 'table',
  GRID = 'grid',
}

export const pinPerformanceTableHeaderTitles = {
  [EngagementMetric.Impressions]: 'Impr.',
  [EngagementMetric.PinClicks]: 'Pin Clicks (Rate)',
  [EngagementMetric.PinClickRate]: '', // Unused - included in `EngagementMetric.PinClicks`
  [EngagementMetric.Saves]: 'Saves (Rate)',
  [EngagementMetric.SaveRate]: '', // Unused - included in `EngagementMetric.Saves`
  [EngagementMetric.OutboundClicks]: 'Outbound Clicks (Rate)',
  [EngagementMetric.OutboundClickRate]: '', // Unused - included in `EngagementMetric.OutboundClicks`
  [EngagementMetric.Engagement]: 'Engagements (Rate)',
  [EngagementMetric.EngagementRate]: '', // Unused - included in `EngagementMetric.Engagement`
};

export const pinPerformanceTableHeaderTooltipTitles = {
  [EngagementMetric.Impressions]: 'Impressions',
  [EngagementMetric.PinClicks]: 'Pin Clicks',
  [EngagementMetric.PinClickRate]: 'Pin Click Rate',
  [EngagementMetric.Saves]: 'Saves',
  [EngagementMetric.SaveRate]: 'Save Rate',
  [EngagementMetric.OutboundClicks]: 'Outbound Clicks',
  [EngagementMetric.OutboundClickRate]: 'Outbound Click Rate',
  [EngagementMetric.Engagement]: 'Engagements',
  [EngagementMetric.EngagementRate]: 'Engagement Rate',
};

export const pinPerformanceTableHeaderTooltips = {
  [EngagementMetric.Impressions]:
    'The number of times your pins were seen on screen.',
  [EngagementMetric.PinClicks]:
    'The total number of clicks on your pins to content on or off Pinterest.',
  [EngagementMetric.PinClickRate]:
    'The total number of clicks divided by the number of impressions for this pin.',
  [EngagementMetric.Saves]: 'The number of times your pins were saved.',
  [EngagementMetric.SaveRate]:
    'The total number of saves divided by the number of impressions for this pin.',
  [EngagementMetric.OutboundClicks]:
    'The total number of clicks to the destination URL associated with your pins.',
  [EngagementMetric.OutboundClickRate]:
    'The total number of clicks to the destination URL divided by the number of impressions for this pin.',
  [EngagementMetric.Engagement]:
    'The total number of pin clicks, outbound clicks, and saves associated with your pins.',
  [EngagementMetric.EngagementRate]:
    'The total engagements divided by the number of impressions for this pin.',
};

export const pinPerformanceTableHeaderPairs = {
  [EngagementMetric.Impressions]: [EngagementMetric.Impressions],
  [EngagementMetric.PinClicks]: [
    EngagementMetric.PinClicks,
    EngagementMetric.PinClickRate,
  ],
  [EngagementMetric.PinClickRate]: [
    EngagementMetric.PinClicks,
    EngagementMetric.PinClickRate,
  ],
  [EngagementMetric.Saves]: [EngagementMetric.Saves, EngagementMetric.SaveRate],
  [EngagementMetric.SaveRate]: [
    EngagementMetric.Saves,
    EngagementMetric.SaveRate,
  ],
  [EngagementMetric.OutboundClicks]: [
    EngagementMetric.OutboundClicks,
    EngagementMetric.OutboundClickRate,
  ],
  [EngagementMetric.OutboundClickRate]: [
    EngagementMetric.OutboundClicks,
    EngagementMetric.OutboundClickRate,
  ],
  [EngagementMetric.Engagement]: [
    EngagementMetric.Engagement,
    EngagementMetric.EngagementRate,
  ],
  [EngagementMetric.EngagementRate]: [
    EngagementMetric.Engagement,
    EngagementMetric.EngagementRate,
  ],
};

export const pinPerformanceTableHeaders = [
  EngagementMetric.Impressions,
  EngagementMetric.PinClicks,
  EngagementMetric.Saves,
  EngagementMetric.OutboundClicks,
  EngagementMetric.Engagement,
];

export const PinterestPrefix = 'pinterest-';
